import { html, nothing } from "lit";
import { customElement } from "lit/decorators.js";

import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/checkbox/checkbox";
import "ui-components/collapsible-section/collapsible-section";
import "./donation-config-panel/donation-config-panel";

import { styles } from "./live-event-tickets-access.css";
import { SignalElement, inject } from "ui-common/di";
import type { ILiveEventFormState, ILiveEventFormService, ContentTier } from "ui-common/services/live-event-form/types";
import type { IBackofficeFeatureFlagsState } from "../../../services/backoffice-feature-flags/types";
import { MAX_PRICE_LIMIT } from "ui-common/form/validators/live-event-validators";
import { numericKeydown, handleFormInput, handleFormBlur } from "ui-common/form/form-utils";
import { t } from "../../../translations/i18n";

@customElement("live-event-tickets-access")
export class LiveEventTicketsAccess extends SignalElement {
  static override styles = styles;

  @inject<ILiveEventFormState>("liveEventFormState")
  private state!: ILiveEventFormState;

  @inject<ILiveEventFormService>("liveEventFormService")
  private service!: ILiveEventFormService;

  @inject<IBackofficeFeatureFlagsState>("backofficeFeatureFlagsState")
  private featureFlags!: IBackofficeFeatureFlagsState;

  private setTier = (tier: ContentTier) => {
    this.service.setContentTier(tier);
  };

  override willUpdate(): void {
    // When only a single tier is enabled by feature flags, auto-pin contentTier
    // to that tier so the rest of the form is consistent with the visible UI.
    const hasOpen = this.featureFlags.hasFeature("open-live-events");
    const hasPaid = this.featureFlags.hasFeature("paid-live-events");
    const hasDonation = this.featureFlags.hasFeature("donation-live-events");
    const enabledCount = [hasOpen, hasPaid, hasDonation].filter(Boolean).length;
    if (enabledCount === 1) {
      const onlyTier: ContentTier = hasOpen ? "free" : hasDonation ? "donation" : "premium";
      if (this.state.contentTier.get() !== onlyTier) {
        this.service.setContentTier(onlyTier);
      }
    }
  }

  /**
   * Returns true when the event is mid-broadcast — donation config must lock
   * once the stream goes live to prevent mid-event target/preset rewrites.
   * The form state does not currently track live status separately from
   * draft/scheduled, so this is a forward-looking guard that returns false
   * until an `eventStatus` signal is wired in by a follow-up task.
   */
  private isStatusLive(): boolean {
    return false;
  }

  override render() {
    const formData = this.state.formData.get();
    const errors = this.state.validationErrors.get();

    const hasOpen = this.featureFlags.hasFeature("open-live-events");
    const hasPaid = this.featureFlags.hasFeature("paid-live-events");
    const hasDonation = this.featureFlags.hasFeature("donation-live-events");

    const enabledCount = [hasOpen, hasPaid, hasDonation].filter(Boolean).length;
    const showSelector = enabledCount > 1;

    const tier = this.state.contentTier.get();
    const showPriceField = tier === "premium" && hasPaid;
    const showDonationPanel = tier === "donation" && hasDonation;

    return html`
      <wow-collapsible-section
        collapsible
        expanded
        header=${t("createLiveEventPage.ticketsAccess")}
        subtitle=${t("createLiveEventPage.ticketsAccessSubtitle")}
      >
        <div class="form-section">
          ${showSelector
            ? html`
                <div class="tier-options">
                  ${hasOpen
                    ? html`
                        <label class="tier-radio">
                          <input
                            type="radio"
                            name="tier"
                            value="free"
                            .checked=${tier === "free"}
                            @change=${() => this.setTier("free")}
                          />
                          <span class="tier-title">${t("createLiveEventPage.freeEvent")}</span>
                          <span class="tier-desc">${t("createLiveEventPage.freeEventHelpText")}</span>
                        </label>
                      `
                    : nothing}
                  ${hasDonation
                    ? html`
                        <label class="tier-radio">
                          <input
                            type="radio"
                            name="tier"
                            value="donation"
                            .checked=${tier === "donation"}
                            @change=${() => this.setTier("donation")}
                          />
                          <span class="tier-title">${t("createLiveEventPage.donationEvent")}</span>
                          <span class="tier-desc">${t("createLiveEventPage.donationEventDescription")}</span>
                        </label>
                      `
                    : nothing}
                  ${hasPaid
                    ? html`
                        <label class="tier-radio">
                          <input
                            type="radio"
                            name="tier"
                            value="premium"
                            .checked=${tier === "premium"}
                            @change=${() => this.setTier("premium")}
                          />
                          <span class="tier-title">${t("createLiveEventPage.paidEvent")}</span>
                          <span class="tier-desc">${t("createLiveEventPage.paidEventDescription")}</span>
                        </label>
                      `
                    : nothing}
                </div>
              `
            : nothing}
          ${showDonationPanel
            ? html`<donation-config-panel ?disabled=${this.isStatusLive()}></donation-config-panel>`
            : nothing}
          <div class="form-row">
            ${showPriceField
              ? html`
                  <wow-input
                    label=${t("createLiveEventPage.pricePerTicket")}
                    type="number"
                    inputmode="decimal"
                    prefix="€"
                    min="0.50"
                    max="${MAX_PRICE_LIMIT}"
                    step="0.01"
                    .value=${formData.feeEuros}
                    error-message=${errors.feeEuros || ""}
                    @input=${(e: Event) => handleFormInput(this.service, "feeEuros", e)}
                    @blur=${() => handleFormBlur(this.service, "feeEuros")}
                  ></wow-input>
                `
              : nothing}
            <wow-input
              label=${t("createLiveEventPage.maxAttendees")}
              type="number"
              inputmode="numeric"
              suffix=${t("createLiveEventPage.tickets")}
              step="1"
              .value=${formData.maxAttendees}
              error-message=${errors.maxAttendees || ""}
              @input=${(e: Event) => handleFormInput(this.service, "maxAttendees", e)}
              @blur=${() => handleFormBlur(this.service, "maxAttendees")}
              @keydown=${numericKeydown}
            ></wow-input>
          </div>
        </div>
      </wow-collapsible-section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-event-tickets-access": LiveEventTicketsAccess;
  }
}
